import React, { useEffect, useState } from 'react';
import { post } from '../../Auths/AuthToken';
import setUiMessage from '../../../Utilities/Tostify';
import { useDispatch, useSelector } from 'react-redux';
import { handAllReferralDocs } from '../../../redux/slices/referralSlice';
import Button from '../../Button';
import { format } from 'date-fns';

function ReportFormPopup({
  onClose,
  patId,
  selectedPat,
  popUpType, //"clinical" | "toc"
  minVisitDate = null,
  maxVisitDate = null,
  selectPurposeOption,
  setFormData,
}) {
  const dispatch = useDispatch();
  const { allReferralDocs } = useSelector((store) => store.referral);
  useEffect(() => {
    (async () => {
      if (patId) {
        const response = await post('api/commonTask/openGenerateReportPopup', {
          patientId: patId,
        });
        if (response.status === 200) {
          setDoctorList(response.data.doctorList);
        }
      }
    })();
  }, [patId]);

  // PURPOSE
  // const [purposeType, setPurposeType] = useState(popUpType);
  const [otherPurpose, setOtherPurpose] = useState('');

  // TO Option
  const [toOption, setToOption] = useState('patient');
  const [toName, setToName] = useState(selectedPat?.patName || '');
  const [doctorValue, setDoctorValue] = useState('');
  const [doctorList, setDoctorList] = useState('');

  // Date Range (only for TOC)
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Clinical Summary Items
  const defaultItems = {
    smokingData: true,
    pendingLabOrders: true,
    clinicInstruction: true,
    futureAppointments: true,
    reasonForReferral: true,
    scheduledLab: true,
    decisionAids: true,
    problems: true,
    medication: true,
    allergies: true,
    labResults: true,
    vitalSign: true,
    procedures: true,
    planOfCare: true,
    immunizationAdminisData: true,
    immunizationData: true,
  };

  const [items, setItems] = useState(defaultItems);
  const [showItemsPopup, setShowItemsPopup] = useState(false);

  const toggleAllItems = (checked) => {
    const updated = {};
    Object.keys(items).forEach((key) => (updated[key] = checked));
    setItems(updated);
  };

  const generateReport = async (type) => {
    try {
      // --- Validation helpers ---
      const validateClinical = () => {
        if (!patId) return setUiMessage('err', 'Patient is not selected');
        if (!toName && toOption !== 'doctor')
          return setUiMessage('err', 'To name is required');
        if (toOption === 'doctor' && !doctorValue)
          return setUiMessage('err', 'Kindly select a doctor');
        return true;
      };

      const validateToc = () => {
        if (!patId) return setUiMessage('err', 'Patient is not selected');
        if (!toName && toOption !== 'doctor')
          return setUiMessage('err', 'To name is required');
        if (toOption === 'doctor' && !doctorValue)
          return setUiMessage('err', 'Kindly select a doctor');
        if (!dateFrom) return setUiMessage('err', 'Select from date');
        if (!dateTo) return setUiMessage('err', 'Select to date');
        return true;
      };

      // --- Select items for clinical ---
      const getSelectedItems = () =>
        Object.keys(items).filter((key) => items[key] === true);

      // --- Document formatter ---
      const createDocObject = (sectionId, sectionName, title, id, dataObj) => ({
        sectionId,
        docsSection: sectionName,
        docsTitle: title,
        docsId: id,
        docsName: dataObj?.file_name,
        docsUrl: dataObj?.file_url,
        docsDate: dataObj?.date || format(new Date(), 'MM-dd-yyyy'),
      });

      // --- Verify visit ---
      const checkVisit = async () => {
        const chk = await post('api/CommonTask/checkVisit', {
          patientId: patId,
        });
        return chk.status === 200 && chk.data?.visitId === '1';
      };

      // ---------------------------
      //       CLINICAL REPORT
      // ---------------------------
      if (type === 'clinical') {
        if (!validateClinical()) return;

        if (!(await checkVisit())) {
          setUiMessage('inf', 'There is no visit for selected patient.');
          return;
        }

        const selectedItems = getSelectedItems();

        const response = await post(
          'api/CommonTask/createClinicalSummaryHl7Report',
          {
            patientId: patId,
            purpose: 'Clinical Summary',
            reportTo:
              toOption === 'doctor'
                ? doctorList.find((item) => item.value === doctorValue)?.name
                : toName,
            mode: 'transmit',
            pdf: 'patRefrl',
            ccrCcdRecvType: toOption,
            reportType: '7',
            selectedElement: selectedItems,
          },
        );

        const isTransmitted =
          response.status === 200 &&
          response.data?.reportId &&
          response.data?.message === 'Transmitted';

        if (!isTransmitted) return;

        const reportResponse = await post('api/CommonTask/getCliSumPdf', {
          reportId: response.data.reportId,
        });

        if (reportResponse.status !== 200) return;

        const dataObj = reportResponse.data;

        const docObj = createDocObject(
          'clinical_summary',
          'clinical summary',
          'Clinical Summary',
          '1',
          dataObj,
        );

        setFormData((prev) => ({
          ...prev,
          clinicalSummary: true,
          clinicalSummaryPdf: dataObj?.file_name,
        }));

        dispatch(handAllReferralDocs([...allReferralDocs, docObj]));
        onClose();
        return;
      }

      // ---------------------------
      //       TOC REPORT
      // ---------------------------
      if (type === 'toc') {
        if (!validateToc()) return;

        if (!(await checkVisit())) {
          setUiMessage('inf', 'There is no visit for selected patient.');
          return;
        }

        const response = await post(
          'api/CommonTask/createTransOfCareHl7Report',
          {
            patientId: patId,
            dateFrom,
            dateTo,
            purpose: '',
            reportTo:
              toOption === 'doctor'
                ? doctorList.find((item) => item.value === doctorValue)?.name
                : toName,
            mode: 'transmit',
            pdf: '1',
            ccrCcdRecvType: toOption,
            ccrCcdRecvName: toOption === 'doctor' ? doctorValue : '',
            reportType: '5',
          },
        );

        const isTransmitted =
          response.status === 200 &&
          response.data?.reportId &&
          response.data?.message === 'Transmitted';

        if (!isTransmitted) return;

        const reportResponse = await post(
          'api/CommonTask/getPatientReportNameById',
          { reportId: response.data.reportId },
        );

        if (reportResponse.status !== 200) return;

        const dataObj = reportResponse.data;

        const docObj = createDocObject(
          'transition_of_care',
          'transition of care',
          'Transition of Care',
          '2',
          dataObj,
        );

        setFormData((prev) => ({
          ...prev,
          transitionOfCare: true,
          transitionOfCarePdf: dataObj?.file_name,
        }));

        dispatch(handAllReferralDocs([...allReferralDocs, docObj]));
        onClose();
      }
    } catch (error) {
      // console.log('working', error?.response?.data?.message);
      setUiMessage('err', error?.response?.data?.message || error?.message);
    }
  };

  return (
    <div className="modalBackground">
      <div className="popup-wrapper gnrate-report-dashbrd-popup">
        <div
          className="flex justify-between item-center px-2"
          style={{ borderBottom: '1px solid #a0ac77' }}
        >
          <span
            className="body-header-text"
            style={{ fontSize: '14px', fontStyle: 'bold' }}
          >
            Generate Report:
          </span>
          {/* <span className="body-header-cross" onClick={onClose}>
            X
          </span> */}
          <Button variant="circle" text="X" onClick={onClose} />
        </div>

        <div className="Form-range">
          {/* PURPOSE */}
          <div className="scnd-row-form">
            <span className="heading-fst">
              Purpose:<span className="required">*</span>
            </span>

            <select
              className="slect"
              disabled
              value={popUpType}
              onChange={(e) => {
                // setPurposeType(e.target.value);
                selectPurposeOption(e.target.value);
              }}
            >
              <option value=""></option>
              <option value="clinical">Clinical Summary</option>
              {/* <option value="echi">
                Electronic copy of health information
              </option> */}
              <option value="toc">Transition of Care</option>
              {/* <option value="other">Other</option> */}
            </select>

            {popUpType === 'other' && (
              <input
                className="purpose-text-bx"
                placeholder="Specify the purpose here"
                value={otherPurpose}
                onChange={(e) => setOtherPurpose(e.target.value)}
                style={{ marginLeft: 25 }}
              />
            )}
          </div>

          {/* DATE RANGE — ONLY FOR TOC */}

          {popUpType === 'toc' && (
            <div
              className="fst-row-form"
              id="ccd-ccr-date-range"
              style={{ display: 'flex', alignItems: 'center', gap: '20px' }}
            >
              <span className="heading-fst">
                Date: <span className="required">*</span>
              </span>

              {/* FROM DATE */}
              <div
                className="tabContainer__input-wrapper flex items-center border border-lightgray rounded-md"
                style={{ width: 160, marginLeft: 5 }}
              >
                {/* <img
                  src="./images/calender.svg"
                  width={18}
                  className="ml-2 opacity-50"
                  alt=""
                /> */}
                <input
                  type="date"
                  className="tabContainer__input flex-1 fs-xs form-control"
                  value={dateFrom ? dateFrom : ''}
                  min={minVisitDate}
                  max={maxVisitDate}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    if (dateTo && e.target.value > dateTo) setDateTo(''); // Reset if invalid
                  }}
                />
              </div>

              <span
                className="frst-row-text clinicalCCRDateSection"
                // style={{ width: 75 }}
              >
                To:
              </span>

              {/* TO DATE */}
              <div
                className="tabContainer__input-wrapper flex items-center border border-lightgray rounded-md"
                style={{ width: 160 }}
              >
                {/* <img
                  src="./images/calender.svg"
                  width={18}
                  className="ml-2 opacity-50"
                  alt=""
                /> */}
                <input
                  type="date"
                  className="tabContainer__input flex-1 fs-xs form-control"
                  value={dateTo ? dateTo : ''}
                  min={dateFrom || minVisitDate}
                  max={maxVisitDate}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* TO */}
          <div className="fst-dottedrow-form">
            <div
              className="scnd-row-form"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span className="heading-fst" style={{ width: '81px' }}>
                To:<span className="required">*</span>
              </span>

              <select
                className="chzn-select slect"
                style={{ width: '120px' }}
                value={toOption}
                onChange={(e) => {
                  setToOption(e.target.value);
                  setToName('');
                }}
              >
                <option value=""></option>
                <option value="doctor">Doctor</option>
                <option value="patient">Patient</option>
                <option value="other">Other</option>
                {/* <option value="authrep">Authorized Representative</option> */}
              </select>

              {toOption === 'doctor' ? (
                <select
                  className="chzn-select-search slect"
                  value={doctorValue}
                  onChange={(e) => setDoctorValue(e.target.value)}
                  style={{ marginLeft: 20, width: '98%' }}
                >
                  <option value=""></option>
                  {doctorList &&
                    doctorList.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                </select>
              ) : (
                <input
                  className="purpose-text-bx"
                  placeholder="Write here"
                  value={toName}
                  onChange={(e) => setToName(e.target.value)}
                  style={{ marginLeft: 20 }}
                />
              )}
            </div>
          </div>

          {/* BUTTONS */}
          <div className="CCD-generate-Button">
            {popUpType !== 'toc' && !showItemsPopup && (
              <Button
                variant="primary"
                onClick={() => {
                  if (!toName) {
                    setUiMessage('err', 'To name is required');
                    return;
                  }
                  setShowItemsPopup(true);
                }}
                text="Generate"
              />
            )}

            {popUpType === 'toc' && (
              <Button
                variant="primary"
                onClick={() => generateReport('toc')}
                text="Generate"
              />
            )}
          </div>
        </div>

        {/* CLINICAL SUMMARY ITEMS POPUP */}
        {showItemsPopup && (
          <div className="modalBackground">
            <div className="clinical-items-popup">
              <div className="ROS-add-edit-header">
                <span className="body-header-text">Clinical Summary</span>
                <Button
                  variant="circle"
                  text="X"
                  onClick={() => setShowItemsPopup(false)}
                />
              </div>

              <div className="ros-left-side cli-smmry-wid-nw">
                <div>
                  <div className="cli-summ-includ-head clinical-summ-wd-nw">
                    <div className="cli-summ-includ-head-brdr"></div>
                    <div className="incuded-head" style={{ width: '260px' }}>
                      Data elements included automatically
                    </div>
                  </div>
                  <div style={{ marginTop: '15px' }}>
                    <span className="included-patient-txt included-wid-nw">
                      Provider’s name and office contact information
                    </span>
                    <span className="included-patient-txt">
                      Date and location of visit
                    </span>
                    <span className="included-patient-txt resn-visit-wid-nw">
                      Reason for visit
                    </span>
                  </div>
                </div>
                <div className="cli-summ-includ-head clinical-summ-wd-nw">
                  <div className="cli-summ-includ-head-brdr"></div>
                  <div
                    className="incuded-head data-elemnt-wd-nw"
                    style={{
                      display: 'flex',
                      gap: '5px',
                      alignItems: 'center',
                      width: '375px',
                    }}
                  >
                    Select data elements to generate clinical summary
                    <input
                      className="clSumItemChekAll"
                      type="checkbox"
                      checked={Object.values(items).every((v) => v)}
                      onChange={(e) => toggleAllItems(e.target.checked)}
                    />
                    All
                  </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {Object.keys(items).map((key) => (
                    <span
                      className="included-patient-txt"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        width: '280px',
                      }}
                      key={key}
                    >
                      <input
                        className="clSumItemChek"
                        type="checkbox"
                        checked={items[key]}
                        onChange={(e) =>
                          setItems({ ...items, [key]: e.target.checked })
                        }
                      />
                      {key}
                    </span>
                  ))}
                </div>
              </div>

              <div className="clinical-summary-report-footer">
                <Button
                  variant="outline"
                  onClick={() => setShowItemsPopup(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => generateReport('clinical')}
                >
                  Generate
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportFormPopup;
